import React, { useEffect, useState, useRef } from 'react';
import { db } from '../../firebase';
import { ref, query, orderByChild, equalTo, get, remove, update } from 'firebase/database';
import { auth } from '../../firebase';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import MobileLayout from '../../components/mobile/MobileLayout';
import ReportsPanel from '../../components/ReportsPanel';

/* ─────────────────────────────────────────────
   TYPES
───────────────────────────────────────────── */
interface Bot {
  id: string; businessName: string; phoneNumberId?: string; accessToken?: string;
  welcomeMessage: string; status: string; createdAt: number; botType?: string;
  aiChannel?: string; phoneNumber?: string; fbPageId?: string; websiteUrl?: string;
  allowedWebsites?: string[]; contactEmail?: string; industry?: string;
}
interface Analytics {
  total: { totalMessages: number; totalUsers: number; totalInteractions: number; };
  daily: Array<{ date: string; messages: number; users: number }>;
  recentConversations: Array<{ message: string; response: string; timestamp: number; userId: string; type: string; }>;
}

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
const BOT_ICONS: Record<string, string> = { whatsapp: '💬', facebook: '📘', website: '🌐', voice: '🎙️', ai: '🤖' };
const BOT_LABELS: Record<string, string> = { whatsapp: 'WhatsApp', facebook: 'Facebook', website: 'Website', voice: 'Voice', ai: 'AI Bot' };
const AI_CHANNEL_ICONS: Record<string, string> = { whatsapp: '💬', website: '🌐', facebook: '📘' };
const AI_CHANNEL_LABELS: Record<string, string> = { whatsapp: 'AI (WhatsApp)', website: 'AI (Website)', facebook: 'AI (Facebook)' };
const TONE_OPTIONS = [
  { value: 'friendly', label: 'Friendly & Casual', hint: 'Warm, conversational — like chatting with a helpful friend.' },
  { value: 'marketer', label: 'Marketer', hint: 'Sells the value and benefit, ends replies with a relevant follow-up.' },
  { value: 'sales',    label: 'Sales-driven', hint: 'Always pushes toward a next step — quote, booking, or contact details.' },
  { value: 'straight', label: 'Straight to the Point', hint: 'Brief and direct, no pitch, no fluff.' },
];

function botIcon(bot: Bot) {
  if (bot.botType === 'ai') return AI_CHANNEL_ICONS[bot.aiChannel || 'website'] || '🤖';
  return BOT_ICONS[bot.botType || 'whatsapp'] || '🤖';
}
function botLabel(bot: Bot) {
  if (bot.botType === 'ai') return AI_CHANNEL_LABELS[bot.aiChannel || 'website'] || 'AI Bot';
  return BOT_LABELS[bot.botType || 'whatsapp'] || 'Bot';
}
function statusColor(status: string) {
  if (status === 'active') return '#10b981';
  if (status === 'pending_verification' || status === 'pending_setup') return '#f59e0b';
  if (status === 'suspended') return '#ef4444';
  if (status === 'pending_payment') return '#f59e0b';
  return '#64748b';
}
function statusLabel(status: string) {
  if (status === 'active') return 'Live';
  if (status === 'pending_verification') return 'Pending';
  if (status === 'pending_setup') return 'Setup';
  if (status === 'suspended') return 'Suspended';
  if (status === 'pending_payment') return 'Unpaid';
  return status;
}
function formatPhone(userId: string): string {
  const digits = userId.replace(/\D/g, '');
  if (digits.startsWith('263') && digits.length === 12) {
    const local = digits.slice(3);
    return `+263 ${local.slice(0, 2)} ${local.slice(2, 5)} ${local.slice(5)}`;
  }
  if (digits.startsWith('27') && digits.length === 11) {
    const local = digits.slice(2);
    return `+27 ${local.slice(0, 2)} ${local.slice(2, 5)} ${local.slice(5)}`;
  }
  if (digits.length >= 10) return `+${digits}`;
  return userId;
}
function generateWebhookSecret(botId: string, businessName: string) {
  const str = `${botId}-${businessName.trim()}-autopilot`;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16).substring(0, 8);
}

const API_BASE = process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_API_URL || '');
const companyName = process.env.REACT_APP_COMPANY_NAME || 'autopilot';
const widgetURL = process.env.REACT_APP_WIDGET_URL || 'https://zimchat.onrender.com';

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
export default function DashboardMobile() {
  const navigate = useNavigate();
  const [bots, setBots] = useState<Bot[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBot, setSelectedBot] = useState<Bot | null>(null);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'conversations' | 'reports' | 'embed'>('overview');
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [userData, setUserData] = useState<any>(null);
  const [hasOverdueInvoice, setHasOverdueInvoice] = useState(false);
  const [showBotPicker, setShowBotPicker] = useState(false);
  const [searchPhone, setSearchPhone] = useState('');
  const [selectedConv, setSelectedConv] = useState<string | null>(null);
  const [contacts, setContacts] = useState<Record<string, any>>({});
  const [agentModes, setAgentModes] = useState<Record<string, boolean>>({});
  const [agentReply, setAgentReply] = useState('');
  const [agentSending, setAgentSending] = useState(false);
  const [isReleasing, setIsReleasing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editingBot, setEditingBot] = useState<Bot | null>(null);
  const [settingsDatasets, setSettingsDatasets] = useState<Array<{ id: string; name: string; format: string }>>([]);
  const [showLegacyKB, setShowLegacyKB] = useState(false);
  const [newWebsiteInput, setNewWebsiteInput] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const releasingRef = useRef<boolean>(false);
  const agentInputRef = useRef<HTMLInputElement>(null);
  const releasedSenders = useRef<Set<string>>(new Set());

  /* ── Data loading ── */
  const loadAnalytics = async (botId: string, silent = false) => {
    if (!silent) { setAnalyticsLoading(true); setAnalytics(null); }
    try {
      const res = await apiFetch(`/api/analytics/${botId}`);
      const data = await res.json();
      if (data.success) setAnalytics(data.data);
    } catch (e) { console.error('Analytics error:', e); }
    finally { if (!silent) setAnalyticsLoading(false); }
  };

  const loadContacts = async (botId: string) => {
    try {
      const snap = await get(ref(db, `contacts/${botId}`));
      if (snap.exists()) {
        const data: Record<string, any> = {};
        snap.forEach(c => { data[c.key!] = c.val(); });
        setContacts(data);
      } else { setContacts({}); }
    } catch (e) { /* silent */ }
  };

  const loadAgentModes = async (botId: string) => {
    try {
      const snap = await get(ref(db, `agentMode/${botId}`));
      const data: Record<string, boolean> = {};
      const now = Date.now();
      const twoHours = 2 * 60 * 60 * 1000;
      if (snap.exists()) {
        const staleKeys: string[] = [];
        snap.forEach(c => {
          const val = c.val();
          if (val?.active) {
            const key = c.key!.replace(/^(wa_|web_|fb_|voice_)/, '');
            const age = now - (val.takenOverAt || 0);
            if (age > twoHours) { staleKeys.push(c.key!); return; }
            if (releasedSenders.current.has(key)) return;
            data[key] = true;
          }
        });
        for (const k of staleKeys) { remove(ref(db, `agentMode/${botId}/${k}`)).catch(() => {}); }
      }
      setAgentModes(prev => {
        const prevKeys = Object.keys(prev).sort().join(',');
        const newKeys = Object.keys(data).sort().join(',');
        if (prevKeys === newKeys) return prev;
        return data;
      });
    } catch (e) { /* silent */ }
  };

  const loadBots = async () => {
    if (!auth.currentUser) { setLoading(false); return; }
    try {
      const q = query(ref(db, 'bots'), orderByChild('userId'), equalTo(auth.currentUser.uid));
      const snap = await get(q);
      const list: Bot[] = [];
      snap.forEach((c) => {
        const bot = { id: c.key!, ...c.val() };
        if (!bot.botType) {
          if (bot.phoneNumberId || bot.accessToken) bot.botType = 'whatsapp';
          else if (bot.fbPageId) bot.botType = 'facebook';
          else if (bot.websiteUrl) bot.botType = 'website';
          else bot.botType = 'whatsapp';
        }
        list.push(bot);
      });
      setBots(list);
      if (list.length > 0 && !selectedBot) {
        setSelectedBot(list[0]);
        loadAnalytics(list[0].id);
        loadContacts(list[0].id);
        loadAgentModes(list[0].id);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const agentEndpoint = (botType?: string) =>
    botType === 'whatsapp' ? 'wa-agent' : botType === 'facebook' ? 'fb-agent' : 'agent';
  const agentChannelLabel = (botType?: string) =>
    botType === 'whatsapp' ? 'WhatsApp' : botType === 'facebook' ? 'Facebook' : 'website';

  const handleTakeOver = async (botId: string, sender: string) => {
    const confirmed = window.confirm(
      `Take Over Conversation\n\nYou are about to take over this ${agentChannelLabel(selectedBot?.botType)} conversation.\n\n• The bot will STOP replying immediately\n• The customer will be notified a human agent joined\n\nContinue?`
    );
    if (!confirmed) return;
    const endpoint = agentEndpoint(selectedBot?.botType);
    const url = `${API_BASE}/api/${endpoint}/takeover/${botId}/${sender}`;
    try {
      const res = await apiFetch(url, {
        method: 'POST',
        body: JSON.stringify({ agentName: currentUser?.displayName || currentUser?.email || 'Agent' }),
      });
      const data = await res.json();
      if (data.success) {
        setAgentModes(prev => ({ ...prev, [sender]: true }));
        if (data.notifyError) alert('Taken over, but could not notify the customer: ' + data.notifyError);
      } else { alert('Failed to take over: ' + (data.error || 'Unknown error')); }
    } catch (e: any) { alert('Failed to take over: ' + e.message); }
  };

  const handleRelease = async (botId: string, sender: string) => {
    if (releasingRef.current || isReleasing) return;
    const confirmed = window.confirm(
      `Return to Bot\n\nHand this conversation back to the automated assistant?\n\n• The customer will be notified the bot is resuming\n• The bot will start responding to new messages\n\nContinue?`
    );
    if (!confirmed) return;
    releasingRef.current = true;
    setIsReleasing(true);
    releasedSenders.current.add(sender);
    const endpoint = agentEndpoint(selectedBot?.botType);
    const url = `${API_BASE}/api/${endpoint}/release/${botId}/${sender}`;
    setAgentModes(prev => { const n = { ...prev }; delete n[sender]; return n; });
    try {
      const res = await apiFetch(url, { method: 'POST' });
      const data = await res.json();
      if (!data.success) {
        setAgentModes(prev => ({ ...prev, [sender]: true }));
        releasedSenders.current.delete(sender);
        alert('Failed to release: ' + (data.error || 'Unknown error'));
      } else if (data.notifyError) {
        alert('Released, but could not notify the customer: ' + data.notifyError);
      }
    } catch (e: any) {
      setAgentModes(prev => ({ ...prev, [sender]: true }));
      releasedSenders.current.delete(sender);
      alert('Failed to release: ' + e.message);
    } finally {
      setTimeout(() => {
        releasingRef.current = false;
        setIsReleasing(false);
        releasedSenders.current.delete(sender);
      }, 10000);
    }
  };

  const handleAgentReply = async (botId: string, sender: string) => {
    if (!agentReply.trim() || agentSending) return;
    setAgentSending(true);
    const endpoint = agentEndpoint(selectedBot?.botType);
    const url = `${API_BASE}/api/${endpoint}/reply/${botId}/${sender}`;
    const messageToSend = agentReply.trim();
    setAgentReply('');
    try {
      const res = await apiFetch(url, {
        method: 'POST',
        body: JSON.stringify({ message: messageToSend, agentName: currentUser?.displayName || currentUser?.email || 'Agent' }),
      });
      const data = await res.json();
      if (!data.success) {
        setAgentReply(messageToSend);
        const msg = data.friendly || data.error || 'Unknown error';
        alert('Failed to send: ' + msg + (data.action && data.actionUrl ? `\n\n${data.action}: ${data.actionUrl}` : ''));
        return;
      }
      setTimeout(() => { if (selectedBot) loadAnalytics(selectedBot.id, true); }, 1000);
    } catch (e: any) { setAgentReply(messageToSend); alert('Network error: ' + e.message); }
    finally { setAgentSending(false); agentInputRef.current?.focus(); }
  };

  const handleSelectBot = (bot: Bot) => {
    setSelectedBot(bot);
    setAnalytics(null);
    setActiveTab('overview');
    setSelectedConv(null);
    setShowBotPicker(false);
    loadAnalytics(bot.id);
    loadContacts(bot.id);
    loadAgentModes(bot.id);
  };

  const handleUpdateBot = async () => {
    if (!editingBot) return;
    try {
      // accessToken lives at botSecrets/{id}, not bots/{id} (broadly
      // readable by any logged-in user) - editingBot.accessToken is only
      // populated when the user typed a new value this session. Write it
      // separately and never include it in the bots/{id} update.
      const newAccessToken = ((editingBot as any).accessToken || '').trim();
      if (newAccessToken) await update(ref(db, `botSecrets/${editingBot.id}`), { accessToken: newAccessToken });
      await update(ref(db, `bots/${editingBot.id}`), {
        businessName: editingBot.businessName,
        welcomeMessage: editingBot.welcomeMessage,
        allowedWebsites: editingBot.allowedWebsites || [],
        phoneNumberId: (editingBot as any).phoneNumberId || '',
        paymentMethods: (editingBot as any).paymentMethods || null,
        aiConfig: (editingBot as any).aiConfig || null,
        emailUser: (editingBot as any).emailUser || '',
        emailPass: (editingBot as any).emailPass || '',
        emailFrom: (editingBot as any).emailFrom || '',
        imapHost: (editingBot as any).imapHost || '',
        imapPort: (editingBot as any).imapPort || 993,
        imapSecure: (editingBot as any).imapSecure !== false,
        smtpHost: (editingBot as any).smtpHost || '',
        smtpPort: (editingBot as any).smtpPort || 587,
        smtpSecure: !!(editingBot as any).smtpSecure,
        updatedAt: Date.now(),
      });
      setShowSettings(false);
      setEditingBot(null);
      setSelectedBot(editingBot);
      loadBots();
      alert('Saved!');
    } catch { alert('Error saving changes'); }
  };

  const handleDeleteBot = async (botId: string) => {
    if (!window.confirm('Delete this bot permanently? All conversations, contacts, campaigns and analytics will be erased. This cannot be undone.')) return;
    try {
      await Promise.all([
        remove(ref(db, `bots/${botId}`)),
        remove(ref(db, `flows/${botId}`)),
        remove(ref(db, `analytics/${botId}`)),
        remove(ref(db, `conversations/${botId}`)),
        remove(ref(db, `contacts/${botId}`)),
        remove(ref(db, `campaigns/${botId}`)),
        remove(ref(db, `campaignLists/${botId}`)),
        remove(ref(db, `campaignListContacts/${botId}`)),
        remove(ref(db, `campaignUnsubscribes/${botId}`)),
        remove(ref(db, `emailLogs/${botId}`)),
        remove(ref(db, `agentMode/${botId}`)),
      ]);
      setShowSettings(false);
      setEditingBot(null);
      setSelectedBot(null);
      loadBots();
    } catch (err: any) {
      console.error('Delete bot error:', err);
      alert('Error deleting bot: ' + (err?.message || err));
    }
  };

  /* ── useEffects ── */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setCurrentUser(user);
        const snap = await get(ref(db, `users/${user.uid}`));
        const ud = snap.val();
        setUserData(ud);
        if (!ud?.onboardingCompleted) { navigate('/onboarding'); return; }
        const invSnap = await get(ref(db, `invoices/${user.uid}`));
        if (invSnap.exists()) {
          let overdue = false;
          invSnap.forEach(inv => { if (inv.val()?.status === 'overdue') overdue = true; });
          setHasOverdueInvoice(overdue);
        }
        loadBots();
      } else { navigate('/login'); }
    });
    return unsub;
    // eslint-disable-next-line
  }, [navigate]);

  useEffect(() => {
    if (activeTab !== 'conversations' || !selectedBot) return;
    const hasActiveAgent = Object.keys(agentModes).length > 0;
    const interval = setInterval(() => {
      loadAnalytics(selectedBot.id, true);
      loadAgentModes(selectedBot.id);
    }, hasActiveAgent ? 3000 : 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [activeTab, selectedBot?.id, Object.keys(agentModes).length]);

  useEffect(() => {
    if (!showSettings || (editingBot?.botType !== 'ai' && (editingBot as any)?.botType !== 'email') || !auth.currentUser?.uid) return;
    setShowLegacyKB(false);
    get(ref(db, `datasets/${auth.currentUser.uid}`)).then(snap => {
      const list: Array<{ id: string; name: string; format: string }> = [];
      if (snap.exists()) snap.forEach((c: any) => { list.push({ id: c.key!, name: c.val().name, format: c.val().format }); });
      setSettingsDatasets(list);
    }).catch(() => {});
  }, [showSettings, editingBot?.id]);

  /* ── conversations ── */
  const threads = analytics?.recentConversations?.reduce((acc, conv) => {
    if (!acc[conv.userId]) acc[conv.userId] = [];
    acc[conv.userId].push(conv);
    return acc;
  }, {} as Record<string, typeof analytics.recentConversations>) || {};

  const threadUsers = Object.keys(threads).filter(uid =>
    threads[uid].some(c => !c.message?.startsWith('[AGENT] '))
  );

  const filteredUsers = threadUsers.filter(uid => {
    if (searchPhone && !uid.includes(searchPhone) && !formatPhone(uid).includes(searchPhone) &&
      !(contacts[uid]?.name || '').toLowerCase().includes(searchPhone.toLowerCase()) &&
      !(contacts[uid]?.customer_name || '').toLowerCase().includes(searchPhone.toLowerCase())) return false;
    if (startDate || endDate) {
      const lastMsg = threads[uid]?.slice(-1)[0];
      if (lastMsg) {
        const d = new Date(lastMsg.timestamp).toISOString().split('T')[0];
        if (startDate && d < startDate) return false;
        if (endDate && d > endDate) return false;
      }
    }
    return true;
  });

  const activeThread = selectedConv ? threads[selectedConv] : null;
  const webhookUrl = editingBot ? `${API_BASE}/webhook/${editingBot.id}/${generateWebhookSecret(editingBot.id, editingBot.businessName)}` : '';

  /* ─────────────────────────────────────────────
     LOADING STATE
  ───────────────────────────────────────────── */
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--bg-page)', color: 'var(--text-primary)', fontFamily: 'system-ui' }}>
        Loading...
      </div>
    );
  }

  /* ─────────────────────────────────────────────
     BOT PICKER BUTTON (header right slot)
  ───────────────────────────────────────────── */
  const BotPickerButton = (
    <button
      onClick={() => setShowBotPicker(true)}
      style={{
        display: 'flex', alignItems: 'center', gap: '5px',
        background: 'var(--bg-element)', border: '1px solid var(--border)',
        borderRadius: '8px', padding: '5px 10px', cursor: 'pointer',
        color: 'var(--text-primary)', fontSize: '13px', fontWeight: '600',
        minHeight: '36px',
      }}
    >
      <span style={{ fontSize: '16px' }}>{selectedBot ? botIcon(selectedBot) : '🤖'}</span>
      <span style={{ maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {selectedBot ? selectedBot.businessName : 'Select bot'}
      </span>
      <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>▾</span>
    </button>
  );

  /* ─────────────────────────────────────────────
     TAB BAR CONFIG
  ───────────────────────────────────────────── */
  const getTabsForBot = (bot: Bot) => {
    const type = bot.botType || 'whatsapp';
    const base = [
      { id: 'overview', icon: '📊', label: 'Overview' },
      { id: 'conversations', icon: '💬', label: 'Chats' },
      { id: 'reports', icon: '📈', label: 'Reports' },
    ];
    if (type === 'website' || (type === 'ai' && bot.aiChannel === 'website')) {
      return [...base, { id: 'embed', icon: '🔌', label: 'Embed' }];
    }
    return base;
  };

  /* ─────────────────────────────────────────────
     STAT CARD
  ───────────────────────────────────────────── */
  const StatCard = ({ icon, val, label, accent }: { icon: string; val: string; label: string; accent?: string }) => (
    <div style={{
      background: accent ? accent + '12' : 'var(--bg-card)',
      borderRadius: '12px',
      border: `1px solid ${accent ? accent + '28' : 'var(--card-border)'}`,
      boxShadow: 'var(--card-shadow)',
      padding: '14px',
    }}>
      <div style={{ fontSize: '18px', marginBottom: '6px' }}>{icon}</div>
      <div style={{ color: 'var(--text-primary)', fontSize: '22px', fontWeight: '700', lineHeight: 1 }}>{val}</div>
      <div style={{ color: 'var(--text-secondary)', fontSize: '11px', marginTop: '4px' }}>{label}</div>
    </div>
  );

  /* ─────────────────────────────────────────────
     BAR CHART
  ───────────────────────────────────────────── */
  const BarChart = ({ data, label }: { data: Array<{ date: string; messages: number }>; label: string }) => {
    const max = Math.max(...data.map(d => d.messages), 1);
    return (
      <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--card-border)', boxShadow: 'var(--card-shadow)', padding: '16px', marginBottom: '14px' }}>
        <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: '600', marginBottom: '14px' }}>{label}</div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '80px' }}>
          {data.slice(-7).map((day, i) => {
            const h = Math.max(4, (day.messages / max) * 80);
            return (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                <div style={{ fontSize: '9px', color: 'var(--text-secondary)', fontWeight: '600' }}>{day.messages || ''}</div>
                <div style={{ width: '100%', background: '#5B4FE8', height: `${h}px`, borderRadius: '3px 3px 0 0', opacity: day.messages ? 1 : 0.2 }} />
                <div style={{ fontSize: '8px', color: 'var(--text-muted)' }}>
                  {new Date(day.date).toLocaleDateString(undefined, { weekday: 'short' })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /* ─────────────────────────────────────────────
     OVERVIEW TAB
  ───────────────────────────────────────────── */
  const OverviewTab = ({ bot }: { bot: Bot }) => {
    const type = bot.botType || 'whatsapp';
    const isPending = bot.status === 'pending_verification' || bot.status === 'pending_setup';

    return (
      <div style={{ padding: '16px' }}>
        {/* Bot info strip */}
        <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--card-border)', padding: '14px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '28px' }}>{botIcon(bot)}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: 'var(--text-primary)', fontSize: '15px', fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{bot.businessName}</div>
            <div style={{ color: 'var(--text-faint)', fontSize: '11px', marginTop: '2px' }}>
              {botLabel(bot)} bot{bot.phoneNumber ? ` · ${bot.phoneNumber}` : ''}{bot.websiteUrl ? ` · ${bot.websiteUrl}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', background: statusColor(bot.status) + '18', border: `1px solid ${statusColor(bot.status)}30`, borderRadius: '20px', padding: '4px 10px', flexShrink: 0 }}>
            <div style={{ width: '6px', height: '6px', borderRadius: '3px', background: statusColor(bot.status) }} />
            <span style={{ fontSize: '11px', color: statusColor(bot.status), fontWeight: '600' }}>{statusLabel(bot.status)}</span>
          </div>
        </div>

        {/* Pending banner */}
        {isPending && (
          <div style={{ background: '#f59e0b11', border: '1px solid #f59e0b30', borderRadius: '10px', padding: '12px 14px', display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '14px' }}>
            <span style={{ fontSize: '16px' }}>⏳</span>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              <span style={{ color: '#f59e0b', fontWeight: '600' }}>Setup in progress. </span>
              {bot.status === 'pending_verification'
                ? "Your WhatsApp number is being registered with Meta. Usually 1–3 business days."
                : "Our team is setting up your bot. We'll email you when it's ready."}
            </div>
          </div>
        )}

        {/* Account warning */}
        {(() => {
          const payStatus = userData?.paymentStatus;
          const accountLapsed = payStatus === 'expired' || payStatus === 'failed' || payStatus === 'cancelled';
          const showBanner = accountLapsed || hasOverdueInvoice;
          if (!showBanner) return null;
          return (
            <Link to="/billing" style={{ textDecoration: 'none', display: 'block', marginBottom: '14px' }}>
              <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: '10px', padding: '12px 14px' }}>
                <div style={{ color: '#f87171', fontSize: '12px', fontWeight: '700', marginBottom: '2px' }}>
                  ⚠️ {accountLapsed ? 'Subscription Lapsed' : 'Overdue Invoice'}
                </div>
                <div style={{ color: 'rgba(248,113,113,0.75)', fontSize: '11px' }}>WhatsApp bots are locked. Tap to go to Billing.</div>
              </div>
            </Link>
          );
        })()}

        {/* Action button */}
        <div style={{ marginBottom: '14px' }}>
          {type === 'voice' ? (
            <Link to={`/builder/voice/${bot.id}`} style={{ display: 'block', textAlign: 'center', background: 'linear-gradient(135deg, #5B4FE8, #7c3aed)', color: 'white', padding: '12px', borderRadius: '10px', textDecoration: 'none', fontSize: '13px', fontWeight: '600' }}>🎙️ Edit voice script</Link>
          ) : type === 'website' ? (
            <Link to={`/builder/website/${bot.id}`} style={{ display: 'block', textAlign: 'center', background: 'linear-gradient(135deg, #5B4FE8, #7c3aed)', color: 'white', padding: '12px', borderRadius: '10px', textDecoration: 'none', fontSize: '13px', fontWeight: '600' }}>🌐 Edit FAQs & widget</Link>
          ) : type === 'facebook' ? (
            <Link to={`/builder/facebook/${bot.id}`} style={{ display: 'block', textAlign: 'center', background: 'linear-gradient(135deg, #5B4FE8, #7c3aed)', color: 'white', padding: '12px', borderRadius: '10px', textDecoration: 'none', fontSize: '13px', fontWeight: '600' }}>📘 Edit replies</Link>
          ) : type === 'ai' ? (
            <button onClick={() => { setEditingBot({ ...bot } as any); setShowSettings(true); }} style={{ display: 'block', width: '100%', textAlign: 'center', background: 'linear-gradient(135deg, #5B4FE8, #7c3aed)', color: 'white', padding: '12px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>🤖 Edit knowledge base</button>
          ) : (
            <div style={{ display: 'flex', gap: '10px' }}>
              <Link to={`/builder/whatsapp/${bot.id}`} style={{ flex: 1, textAlign: 'center', background: 'linear-gradient(135deg, #5B4FE8, #7c3aed)', color: 'white', padding: '12px', borderRadius: '10px', textDecoration: 'none', fontSize: '13px', fontWeight: '600' }}>💬 Edit flow</Link>
              <Link to={`/campaigns/${bot.id}`} style={{ flex: 1, textAlign: 'center', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: 'white', padding: '12px', borderRadius: '10px', textDecoration: 'none', fontSize: '13px', fontWeight: '600' }}>📢 Campaigns</Link>
            </div>
          )}
        </div>

        {/* Settings button */}
        <button
          onClick={async () => { const snap = await get(ref(db, `bots/${bot.id}`)); setEditingBot({ id: bot.id, ...snap.val() }); setShowSettings(true); }}
          style={{ width: '100%', padding: '11px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '10px', color: 'var(--text-secondary)', fontSize: '13px', cursor: 'pointer', marginBottom: '16px' }}
        >
          ⚙️ Settings
        </button>

        {/* AI embed code strip */}
        {type === 'ai' && bot.aiChannel === 'website' && (
          <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--card-border)', padding: '14px', marginBottom: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600' }}>Embed code</div>
              <button onClick={() => { navigator.clipboard.writeText(`<script src="${widgetURL}/widget.js" data-bot-id="${bot.id}"${API_BASE ? ` data-api-url="${API_BASE}"` : ''}></script>`); alert('Copied!'); }}
                style={{ background: '#5B4FE8', border: 'none', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>Copy</button>
            </div>
            <div style={{ background: 'var(--bg-page)', borderRadius: '7px', padding: '10px', border: '1px solid var(--card-border)' }}>
              <code style={{ color: '#818cf8', fontSize: '11px', wordBreak: 'break-all' }}>
                {`<script src="${widgetURL}/widget.js" data-bot-id="${bot.id}"${API_BASE ? ` data-api-url="${API_BASE}"` : ''}></script>`}
              </code>
            </div>
          </div>
        )}

        {/* AI info strip */}
        {type === 'ai' && (
          <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--card-border)', padding: '14px', marginBottom: '14px', display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '10px', color: 'var(--text-faint)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '3px' }}>Channel</div>
              <div style={{ color: 'var(--text-primary)', fontSize: '13px' }}>{bot.aiChannel === 'whatsapp' ? '💬 WhatsApp' : bot.aiChannel === 'facebook' ? '📘 Facebook' : '🌐 Website'}</div>
            </div>
            <div>
              <div style={{ fontSize: '10px', color: 'var(--text-faint)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '3px' }}>Knowledge base</div>
              <div style={{ color: ((bot as any).aiConfig?.datasetId || (bot as any).aiConfig?.knowledgeBase) ? '#10b981' : '#f59e0b', fontSize: '13px' }}>
                {(bot as any).aiConfig?.datasetId ? '✓ Dataset' : (bot as any).aiConfig?.knowledgeBase ? `✓ ${((bot as any).aiConfig.knowledgeBase as string).length.toLocaleString()} chars` : '⚠️ Not set'}
              </div>
            </div>
          </div>
        )}

        {/* Voice bot */}
        {type === 'voice' && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎙️</div>
            <div style={{ color: 'var(--text-primary)', fontSize: '16px', fontWeight: '600', marginBottom: '6px' }}>{bot.businessName}</div>
            <div style={{ color: 'var(--text-faint)', fontSize: '12px', marginBottom: '20px' }}>Voice bot · {bot.websiteUrl || 'No website set'}</div>
          </div>
        )}

        {/* Analytics loading */}
        {analyticsLoading && (
          <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-faint)', fontSize: '13px' }}>Loading analytics...</div>
        )}

        {/* No data yet */}
        {!analyticsLoading && !analytics && type !== 'voice' && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: '36px', marginBottom: '10px' }}>{botIcon(bot)}</div>
            <div style={{ color: 'var(--text-faint)', fontSize: '13px' }}>
              {type === 'whatsapp' ? 'No messages yet. Share your WhatsApp number to get started.' :
               type === 'facebook' ? 'No messages yet. Make sure your Facebook Page is connected.' :
               type === 'website' ? 'No chats yet. Add the embed code to your website.' :
               'No conversations yet. Share your bot link to get started.'}
            </div>
          </div>
        )}

        {/* Stats */}
        {!analyticsLoading && analytics && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
              <StatCard icon="💬" val={analytics.total.totalMessages.toLocaleString()} label={type === 'website' ? 'Visitor chats' : type === 'facebook' ? 'Messages' : 'Messages received'} />
              <StatCard icon="👥" val={analytics.total.totalUsers.toLocaleString()} label={type === 'facebook' ? 'Followers engaged' : 'Unique users'} />
              <StatCard icon="⚡"
                val={analytics.total.totalMessages > 0
                  ? `${Math.round(((analytics.total.totalInteractions || analytics.total.totalMessages) / analytics.total.totalMessages) * 100)}%`
                  : '—'}
                label={type === 'facebook' ? 'Auto-replied' : type === 'website' ? 'FAQ hit rate' : 'Auto-handled'}
                accent="#10b981"
              />
              <StatCard icon="📅"
                val={analytics.daily.length > 0
                  ? (analytics.total.totalMessages / Math.max(analytics.daily.filter(d => d.messages > 0).length, 1)).toFixed(1)
                  : '0'}
                label="Avg / day"
                accent="#5B4FE8"
              />
            </div>

            <BarChart
              data={analytics.daily}
              label={type === 'website' ? 'Visitor chats last 7 days' : type === 'ai' ? 'Conversations last 7 days' : 'Messages last 7 days'}
            />

            {/* Recent conversations preview */}
            {analytics.recentConversations.length > 0 && (
              <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--card-border)', padding: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: '700' }}>Recent conversations</div>
                  <button onClick={() => setActiveTab('conversations')} style={{ background: 'var(--bg-element)', border: '1px solid var(--border)', color: 'var(--text-primary)', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer', fontWeight: '600' }}>View all →</button>
                </div>
                {analytics.recentConversations.slice(0, 5).map((conv, i) => {
                  const contact = contacts[conv.userId] || {};
                  const displayName = contact.name || contact.customer_name;
                  return (
                    <div
                      key={i}
                      onClick={() => { setActiveTab('conversations'); setSelectedConv(conv.userId); }}
                      style={{ padding: '10px 12px', background: 'var(--bg-element)', borderRadius: '9px', marginBottom: '8px', border: '1px solid var(--border)', cursor: 'pointer' }}
                    >
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px', fontWeight: '600' }}>
                        {displayName ? `${displayName} · ` : ''}{formatPhone(conv.userId)} · {new Date(conv.timestamp).toLocaleString()}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-primary)', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>👤 {conv.message}</div>
                      <div style={{ fontSize: '12px', color: '#5B4FE8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>🤖 {conv.response.slice(0, 80)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  /* ─────────────────────────────────────────────
     CONVERSATIONS TAB
  ───────────────────────────────────────────── */
  const ConversationsTab = ({ bot }: { bot: Bot }) => {
    // Thread view
    if (selectedConv && activeThread) {
      const isAgentActive = agentModes[selectedConv];
      const contact = contacts[selectedConv] || {};
      const displayName = contact.name || contact.customer_name;

      return (
        <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 116px)' }}>
          {/* Thread header */}
          <div style={{ padding: '12px 16px', background: 'var(--bg-card)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
            <button onClick={() => setSelectedConv(null)} style={{ background: 'none', border: 'none', color: 'var(--text-primary)', fontSize: '20px', cursor: 'pointer', padding: '4px', lineHeight: 1, flexShrink: 0 }}>←</button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {displayName || formatPhone(selectedConv)}
              </div>
              {displayName && <div style={{ color: 'var(--text-faint)', fontSize: '10px', fontFamily: 'monospace' }}>{formatPhone(selectedConv)}</div>}
              {contact.email && <div style={{ color: 'var(--text-faint)', fontSize: '10px' }}>📧 {contact.email}</div>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              {isAgentActive ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#10b98115', border: '1px solid #10b98130', borderRadius: '20px', padding: '3px 8px' }}>
                    <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#10b981' }} />
                    <span style={{ fontSize: '10px', color: '#10b981', fontWeight: '600' }}>Agent</span>
                  </div>
                  <button
                    onClick={() => !isReleasing && handleRelease(bot.id, selectedConv)}
                    disabled={isReleasing}
                    style={{ padding: '6px 10px', background: isReleasing ? 'var(--bg-element)' : 'var(--bg-element)', color: isReleasing ? 'var(--text-faint)' : 'var(--text-secondary)', border: '1px solid var(--card-border)', borderRadius: '8px', cursor: isReleasing ? 'not-allowed' : 'pointer', fontSize: '11px', opacity: isReleasing ? 0.5 : 1 }}
                  >
                    {isReleasing ? '⏳' : '🤖 Bot'}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleTakeOver(bot.id, selectedConv)}
                  style={{ padding: '6px 12px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}
                >
                  {bot?.botType === 'whatsapp' ? '💬 Take Over' : '🙋 Take Over'}
                </button>
              )}
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column-reverse', gap: '10px' }}>
            <div ref={messagesEndRef} style={{ height: 1 }} />
            {[...activeThread].map((conv, i) => {
              const isAgentMsg = conv.message?.startsWith('[AGENT] ');
              const agentText = isAgentMsg ? conv.message.replace('[AGENT] ', '') : null;

              if (isAgentMsg) {
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <div style={{ maxWidth: '78%', background: 'var(--bg-page)', border: '1px solid #10b98140', borderLeft: '3px solid #10b981', borderRadius: '4px 12px 12px 12px', padding: '9px 12px' }}>
                      <div style={{ fontSize: '9px', color: '#10b981', fontWeight: '600', marginBottom: '3px' }}>👤 Agent</div>
                      <div style={{ color: 'var(--text-primary)', fontSize: '13px', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{agentText}</div>
                      <div style={{ color: 'var(--text-faint)', fontSize: '9px', marginTop: '3px' }}>
                        {new Date(conv.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div key={i}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '5px' }}>
                    <div style={{ maxWidth: '78%', background: '#5B4FE8', borderRadius: '12px 12px 4px 12px', padding: '9px 12px' }}>
                      <div style={{ color: 'white', fontSize: '13px', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{conv.message}</div>
                      <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '9px', marginTop: '3px', textAlign: 'right' }}>
                        👤 {new Date(conv.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                  {conv.response && conv.response !== '[waiting for agent]' && (
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <div style={{ maxWidth: '78%', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '4px 12px 12px 12px', padding: '9px 12px' }}>
                        <div style={{ color: 'var(--text-primary)', fontSize: '13px', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{conv.response}</div>
                        <div style={{ color: 'var(--text-faint)', fontSize: '9px', marginTop: '3px' }}>
                          🤖 {new Date(conv.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Contact details */}
          {contact && Object.keys(contact).filter(k => !['updatedAt', 'phone'].includes(k)).length > 0 && (
            <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: '16px', flexWrap: 'wrap', background: 'var(--bg-page)', flexShrink: 0 }}>
              {Object.entries(contact)
                .filter(([k]) => !['updatedAt', 'phone'].includes(k))
                .map(([k, v]) => (
                  <div key={k}>
                    <div style={{ fontSize: '9px', color: 'var(--text-faint)', textTransform: 'capitalize', marginBottom: '1px' }}>{k.replace(/_/g, ' ')}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-primary)', fontWeight: '500' }}>{String(v)}</div>
                  </div>
                ))}
            </div>
          )}

          {/* Agent reply input */}
          {isAgentActive && (
            <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', background: 'var(--bg-page)', display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '24px', padding: '8px 14px', gap: '8px' }}>
                <span style={{ fontSize: '12px' }}>👤</span>
                <input
                  ref={agentInputRef}
                  type="text"
                  value={agentReply}
                  onChange={e => setAgentReply(e.target.value)}
                  onKeyDown={e => {
                    e.nativeEvent.stopImmediatePropagation();
                    e.stopPropagation();
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAgentReply(bot.id, selectedConv); }
                  }}
                  onKeyUp={e => { e.nativeEvent.stopImmediatePropagation(); e.stopPropagation(); }}
                  placeholder={bot?.botType === 'whatsapp' ? 'Reply to customer...' : 'Type reply as agent...'}
                  style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: '13px' }}
                  autoComplete="off"
                  autoFocus
                />
              </div>
              <button
                onClick={() => handleAgentReply(bot.id, selectedConv)}
                disabled={agentSending || !agentReply.trim()}
                style={{ padding: '8px 14px', background: agentSending || !agentReply.trim() ? 'var(--bg-element)' : '#10b981', color: agentSending || !agentReply.trim() ? 'var(--text-faint)' : 'white', border: 'none', borderRadius: '20px', cursor: agentSending || !agentReply.trim() ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap' }}
              >
                {agentSending ? '...' : '➤'}
              </button>
            </div>
          )}
        </div>
      );
    }

    // Contact list
    return (
      <div>
        {/* Search + filter bar */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-card)' }}>
          <input
            type="text"
            placeholder="Search name or number..."
            value={searchPhone}
            onChange={e => { setSearchPhone(e.target.value); setSelectedConv(null); }}
            style={{ width: '100%', padding: '9px 12px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const, marginBottom: '8px' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button
              onClick={() => setShowDateFilter(!showDateFilter)}
              style={{ background: showDateFilter ? '#5B4FE820' : 'var(--bg-element)', border: `1px solid ${showDateFilter ? '#5B4FE840' : 'var(--border)'}`, color: showDateFilter ? '#818cf8' : 'var(--text-muted)', padding: '6px 12px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer' }}
            >
              📅 Date filter {showDateFilter ? '▲' : '▼'}
            </button>
            <span style={{ fontSize: '11px', color: 'var(--text-faint)' }}>{filteredUsers.length} contacts</span>
          </div>
          {showDateFilter && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '10px', color: 'var(--text-faint)', marginBottom: '3px' }}>From</div>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                  style={{ width: '100%', padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '7px', color: 'var(--text-primary)', fontSize: '12px', outline: 'none', boxSizing: 'border-box' as const }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '10px', color: 'var(--text-faint)', marginBottom: '3px' }}>To</div>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                  style={{ width: '100%', padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '7px', color: 'var(--text-primary)', fontSize: '12px', outline: 'none', boxSizing: 'border-box' as const }} />
              </div>
            </div>
          )}
        </div>

        {/* Contact cards */}
        {filteredUsers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-faint)', fontSize: '13px' }}>
            {analytics ? 'No conversations match your filter.' : analyticsLoading ? 'Loading conversations...' : 'No conversations yet.'}
          </div>
        ) : (
          filteredUsers.map(uid => {
            const contact = contacts[uid] || {};
            const displayName = contact.name || contact.customer_name || null;
            const lastMsg = threads[uid]?.filter(c => !c.message?.startsWith('[AGENT] ')).slice(-1)[0];
            const msgCount = threads[uid]?.filter(c => !c.message?.startsWith('[AGENT] ')).length || 0;
            const isAgentActive = agentModes[uid];
            return (
              <div
                key={uid}
                onClick={() => setSelectedConv(uid)}
                style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', cursor: 'pointer', display: 'flex', gap: '12px', alignItems: 'flex-start', minHeight: '64px' }}
              >
                <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: isAgentActive ? '#10b98122' : '#5B4FE820', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0, border: isAgentActive ? '1.5px solid #10b98140' : '1.5px solid #5B4FE840' }}>
                  {isAgentActive ? '👤' : '💬'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                    <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {displayName || formatPhone(uid)}
                    </div>
                    <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '5px' }}>
                      {isAgentActive && (
                        <span style={{ background: '#10b98118', color: '#10b981', fontSize: '9px', fontWeight: '700', padding: '1px 5px', borderRadius: '8px', border: '1px solid #10b98130' }}>LIVE</span>
                      )}
                      <div style={{ background: '#5B4FE820', color: '#818cf8', fontSize: '10px', padding: '2px 7px', borderRadius: '8px', fontWeight: '600' }}>
                        {msgCount}
                      </div>
                    </div>
                  </div>
                  {displayName && <div style={{ color: 'var(--text-faint)', fontSize: '10px', fontFamily: 'monospace', marginTop: '1px' }}>{formatPhone(uid)}</div>}
                  {lastMsg && (
                    <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {lastMsg.message.slice(0, 40)}
                    </div>
                  )}
                  {lastMsg && (
                    <div style={{ color: 'var(--text-faint)', fontSize: '10px', marginTop: '2px' }}>
                      {new Date(lastMsg.timestamp).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}

        {/* Export CSV */}
        {filteredUsers.length > 0 && (
          <div style={{ padding: '14px 16px' }}>
            <button
              onClick={() => {
                const csv = (analytics?.recentConversations || []).map(c => {
                  const contact = contacts[c.userId] || {};
                  return `"${formatPhone(c.userId)}","${contact.name || contact.customer_name || ''}","${c.message.replace(/"/g, '')}","${c.response.replace(/"/g, '')}","${new Date(c.timestamp).toLocaleString()}"`;
                }).join('\n');
                const blob = new Blob(['Phone,Name,Message,Response,Time\n' + csv], { type: 'text/csv' });
                const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
                a.download = `conversations_${new Date().toISOString().split('T')[0]}.csv`; a.click();
              }}
              style={{ width: '100%', padding: '11px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', color: 'var(--text-secondary)', borderRadius: '10px', fontSize: '13px', cursor: 'pointer' }}
            >
              Export CSV
            </button>
          </div>
        )}
      </div>
    );
  };

  /* ─────────────────────────────────────────────
     EMBED TAB
  ───────────────────────────────────────────── */
  const EmbedTab = ({ bot }: { bot: Bot }) => {
    const scriptTag = `<script src="${widgetURL}/widget.js" data-bot-id="${bot.id}"${API_BASE ? ` data-api-url="${API_BASE}"` : ''}></script>`;
    const webhookSecret = generateWebhookSecret(bot.id, bot.businessName);
    const wh = `${API_BASE}/webhook/${bot.id}/${webhookSecret}`;
    const steps = [
      { n: '1', title: 'Copy the code', desc: 'Tap "Copy code" above.' },
      { n: '2', title: 'Open your website editor', desc: 'Works on WordPress, Wix, Squarespace, or plain HTML.' },
      { n: '3', title: 'Paste before </body>', desc: 'Find the closing </body> tag in your theme footer and paste just before it.' },
      { n: '4', title: 'Publish and test', desc: 'Save and publish. The chat bubble appears bottom-right.' },
    ];

    return (
      <div style={{ padding: '16px' }}>
        {/* Widget embed */}
        <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--card-border)', padding: '16px', marginBottom: '14px' }}>
          <div style={{ color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600', marginBottom: '12px' }}>Chat widget embed code</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>Paste before {'</body>'} on your site</div>
            <button
              onClick={() => { navigator.clipboard.writeText(scriptTag); alert('Copied!'); }}
              style={{ background: '#5B4FE8', border: 'none', color: 'white', padding: '6px 14px', borderRadius: '7px', fontSize: '12px', cursor: 'pointer', flexShrink: 0 }}
            >Copy code</button>
          </div>
          <div style={{ background: 'var(--bg-page)', borderRadius: '8px', padding: '12px', border: '1px solid var(--card-border)' }}>
            <code style={{ color: '#818cf8', fontSize: '11px', wordBreak: 'break-all', display: 'block' }}>{scriptTag}</code>
          </div>
        </div>

        {/* Steps */}
        <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--card-border)', padding: '16px', marginBottom: '14px' }}>
          <div style={{ color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600', marginBottom: '14px' }}>How to embed</div>
          {steps.map(s => (
            <div key={s.n} style={{ display: 'flex', gap: '12px', marginBottom: '14px', alignItems: 'flex-start' }}>
              <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#5B4FE8', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', flexShrink: 0 }}>{s.n}</div>
              <div>
                <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: '600', marginBottom: '2px' }}>{s.title}</div>
                <div style={{ color: 'var(--text-faint)', fontSize: '12px' }}>{s.desc}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Webhook URL (for WA / AI WA bots) */}
        {(bot.botType === 'whatsapp' || (bot.botType === 'ai' && bot.aiChannel === 'whatsapp') || (!bot.botType && bot.phoneNumberId)) && (
          <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--card-border)', padding: '16px', marginBottom: '14px' }}>
            <div style={{ color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600', marginBottom: '12px' }}>Webhook URL</div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <input readOnly value={wh} style={{ flex: 1, padding: '9px 10px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '7px', color: 'var(--text-muted)', fontSize: '11px', outline: 'none', minWidth: 0 }} />
              <button onClick={() => { navigator.clipboard.writeText(wh); alert('Copied!'); }}
                style={{ padding: '9px 14px', background: '#5B4FE8', color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', flexShrink: 0 }}>Copy</button>
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-faint)' }}>
              Verify token: <code style={{ background: 'var(--bg-element)', padding: '1px 6px', borderRadius: '4px', color: 'var(--text-secondary)' }}>{bot.businessName}_{new Date().getFullYear()}</code>
            </div>
          </div>
        )}

        {/* Bot ID info */}
        <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--card-border)', padding: '16px' }}>
          <div style={{ color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600', marginBottom: '10px' }}>Bot info</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
            <span style={{ color: 'var(--text-faint)', fontSize: '12px' }}>Bot ID</span>
            <code style={{ color: 'var(--text-primary)', fontSize: '11px', background: 'var(--bg-element)', padding: '2px 8px', borderRadius: '5px' }}>{bot.id}</code>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
            <span style={{ color: 'var(--text-faint)', fontSize: '12px' }}>Widget URL</span>
            <a href={widgetURL} target="_blank" rel="noopener noreferrer" style={{ color: '#818cf8', fontSize: '11px' }}>{widgetURL}</a>
          </div>
        </div>
      </div>
    );
  };

  /* ─────────────────────────────────────────────
     SETTINGS MODAL
  ───────────────────────────────────────────── */
  const SettingsModal = () => {
    if (!showSettings || !editingBot) return null;
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 200, display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, overflowY: 'auto', background: 'var(--bg-page)' }}>
          {/* Settings header */}
          <div style={{ position: 'sticky', top: 0, background: 'var(--bg-sidebar)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', padding: '0 16px', height: '52px', gap: '12px', zIndex: 10 }}>
            <button onClick={() => { setShowSettings(false); setEditingBot(null); }} style={{ background: 'none', border: 'none', color: 'var(--text-primary)', fontSize: '20px', cursor: 'pointer', padding: '4px' }}>←</button>
            <div style={{ flex: 1, fontWeight: 700, fontSize: '16px', color: 'var(--text-primary)' }}>Bot settings</div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'var(--bg-element)', padding: '3px 10px', borderRadius: '16px' }}>
              <span style={{ fontSize: '13px' }}>{botIcon(editingBot)}</span>
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{botLabel(editingBot)}</span>
            </div>
          </div>

          <div style={{ padding: '16px' }}>
            {/* Basic fields */}
            {[
              { label: 'Business Name', key: 'businessName', type: 'text', placeholder: 'My Business' },
              { label: 'Welcome Message', key: 'welcomeMessage', type: 'textarea', placeholder: 'Hello! How can I help?' },
            ].map(field => (
              <div key={field.key} style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: '6px' }}>{field.label}</label>
                {field.type === 'textarea' ? (
                  <textarea value={(editingBot as any)[field.key]} onChange={e => setEditingBot({ ...editingBot, [field.key]: e.target.value })} rows={3}
                    style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', resize: 'vertical', boxSizing: 'border-box' as const }} />
                ) : (
                  <input type={field.type} value={(editingBot as any)[field.key] || ''} onChange={e => setEditingBot({ ...editingBot, [field.key]: e.target.value })} placeholder={field.placeholder}
                    style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const }} />
                )}
              </div>
            ))}

            {/* WhatsApp credentials */}
            {(editingBot.botType === 'whatsapp' || !editingBot.botType) && (
              <>
                {[
                  { label: 'Phone Number ID', key: 'phoneNumberId', placeholder: '1234567890', type: 'text' },
                  { label: 'Access Token', key: 'accessToken', placeholder: 'Already connected — leave blank to keep', type: 'password' },
                ].map(field => (
                  <div key={field.key} style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: '6px' }}>{field.label}</label>
                    <input type={field.type} value={(editingBot as any)[field.key] || ''} onChange={e => setEditingBot({ ...editingBot, [field.key]: e.target.value })} placeholder={field.placeholder}
                      style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const }} />
                  </div>
                ))}
                <div style={{ marginBottom: '14px', padding: '12px', background: 'var(--bg-card)', borderRadius: '8px', border: '1px solid var(--card-border)' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: '8px' }}>Webhook URL</div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input readOnly value={webhookUrl}
                      style={{ flex: 1, padding: '8px 10px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '7px', color: 'var(--text-muted)', fontSize: '11px', outline: 'none', minWidth: 0 }} />
                    <button onClick={() => { navigator.clipboard.writeText(webhookUrl); alert('Copied!'); }}
                      style={{ padding: '8px 14px', background: '#5B4FE8', color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', flexShrink: 0 }}>Copy</button>
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '6px' }}>
                    Verify token: <code style={{ background: 'var(--bg-page)', padding: '1px 6px', borderRadius: '4px', color: 'var(--text-secondary)' }}>{editingBot.businessName}_{new Date().getFullYear()}</code>
                  </div>
                </div>
              </>
            )}

            {/* Email bot credentials */}
            {(editingBot as any).botType === 'email' && (
              <>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: '10px' }}>Email Credentials</div>
                {[
                  { label: 'Email Address', key: 'emailUser', type: 'email', placeholder: 'you@example.com' },
                  { label: 'App Password', key: 'emailPass', type: 'password', placeholder: 'App password (not your login password)' },
                  { label: 'Display Name', key: 'emailFrom', type: 'text', placeholder: 'My Business <you@example.com>' },
                  { label: 'IMAP Host', key: 'imapHost', type: 'text', placeholder: 'imap.gmail.com' },
                  { label: 'SMTP Host', key: 'smtpHost', type: 'text', placeholder: 'smtp.gmail.com' },
                ].map(field => (
                  <div key={field.key} style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: '6px' }}>{field.label}</label>
                    <input type={field.type} value={(editingBot as any)[field.key] || ''} onChange={e => setEditingBot({ ...editingBot, [field.key]: e.target.value } as any)} placeholder={field.placeholder}
                      style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const }} />
                  </div>
                ))}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: '6px' }}>IMAP Port</label>
                    <input type="number" value={(editingBot as any).imapPort || 993} onChange={e => setEditingBot({ ...editingBot, imapPort: Number(e.target.value) } as any)}
                      style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: '6px' }}>SMTP Port</label>
                    <input type="number" value={(editingBot as any).smtpPort || 587} onChange={e => setEditingBot({ ...editingBot, smtpPort: Number(e.target.value) } as any)}
                      style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const }} />
                  </div>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: '10px', marginTop: '4px' }}>AI Configuration</div>
              </>
            )}

            {/* AI bot config */}
            {(editingBot.botType === 'ai' || (editingBot as any).botType === 'email') && (
              <>
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: '6px' }}>Tone</label>
                  <select
                    value={(editingBot as any).aiConfig?.tone || 'friendly'}
                    onChange={e => setEditingBot({ ...editingBot, aiConfig: { ...(editingBot as any).aiConfig, tone: e.target.value } } as any)}
                    style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const }}
                  >
                    {TONE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '4px' }}>
                    {TONE_OPTIONS.find(t => t.value === ((editingBot as any).aiConfig?.tone || 'friendly'))?.hint}
                  </div>
                </div>
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: '6px' }}>System Instructions <span style={{ color: 'var(--text-faint)', textTransform: 'none' as const, fontWeight: '400' }}>(optional)</span></label>
                  <textarea
                    value={(editingBot as any).aiConfig?.systemInstruction || ''}
                    onChange={e => setEditingBot({ ...editingBot, aiConfig: { ...(editingBot as any).aiConfig, systemInstruction: e.target.value } } as any)}
                    rows={3}
                    placeholder="e.g. Always reply in Shona. Never discuss competitor products."
                    style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', resize: 'vertical', boxSizing: 'border-box' as const }}
                  />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                    {[
                      { label: 'Sales focus', text: 'Always highlight the value and benefits of our products. Guide the customer toward making a purchase or booking. When appropriate, mention any current offers or promotions. End every reply by inviting them to take the next step.' },
                      { label: 'Customer support', text: 'Be empathetic and solution-focused. Always acknowledge the customer\'s concern before answering. If you cannot resolve an issue, offer to escalate it to a human agent. Never leave a customer without a next step.' },
                      { label: 'Local language', text: 'If the customer writes in Shona or Ndebele, reply in the same language. If they write in English, reply in English. Always be respectful and culturally appropriate.' },
                      { label: 'Strict KB only', text: 'Only answer questions that are directly answered in the knowledge base. For anything outside the knowledge base, politely say you don\'t have that information and ask them to contact the team directly. Never guess or make up information.' },
                      { label: 'Appointment booking', text: 'Your main goal is to help customers book an appointment or schedule a consultation. Collect their name, preferred date and time, and the service they need. Confirm all details before ending the conversation.' },
                      { label: 'Professional email', text: 'Always write in a formal, professional email tone. Use proper greetings and sign-offs. Structure responses with clear paragraphs. Avoid slang or casual language. Sign off with the business name.' },
                    ].map(p => (
                      <button key={p.label} type="button"
                        onClick={() => setEditingBot({ ...editingBot, aiConfig: { ...(editingBot as any).aiConfig, systemInstruction: p.text } } as any)}
                        style={{ padding: '4px 10px', borderRadius: '20px', border: '1px solid var(--card-border)', background: 'var(--bg-element)', color: 'var(--text-secondary)', fontSize: '11px', cursor: 'pointer', fontWeight: '500' }}>
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' as const, letterSpacing: '0.06em' }}>Knowledge Base Dataset</label>
                    <Link to="/datasets" target="_blank" style={{ fontSize: '11px', color: '#818cf8', textDecoration: 'none' }}>Manage ↗</Link>
                  </div>
                  {settingsDatasets.length === 0 ? (
                    <div style={{ background: 'var(--bg-element)', borderRadius: '8px', padding: '12px', fontSize: '12px', color: 'var(--text-muted)' }}>
                      No datasets yet.{' '}
                      <Link to="/datasets" target="_blank" style={{ color: '#818cf8' }}>Upload one</Link>
                      {' '}then reopen Settings to select it.
                    </div>
                  ) : (
                    <select
                      value={(editingBot as any).aiConfig?.datasetId || ''}
                      onChange={e => setEditingBot({ ...editingBot, aiConfig: { ...(editingBot as any).aiConfig, datasetId: e.target.value || null } } as any)}
                      style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const }}
                    >
                      <option value="">— No dataset (use inline text below) —</option>
                      {settingsDatasets.map(d => <option key={d.id} value={d.id}>{d.name} ({d.format})</option>)}
                    </select>
                  )}
                </div>
                <div style={{ marginBottom: '14px' }}>
                  <button type="button" onClick={() => setShowLegacyKB(v => !v)}
                    style={{ background: 'none', border: 'none', color: 'var(--text-faint)', cursor: 'pointer', fontSize: '11px', padding: 0, marginBottom: '6px' }}>
                    {showLegacyKB ? '▾' : '▸'} {(editingBot as any).aiConfig?.knowledgeBase ? 'Edit inline text (legacy)' : 'Or type inline text instead'}
                  </button>
                  {showLegacyKB && (
                    <>
                      <div style={{ fontSize: '12px', color: 'var(--text-faint)', marginBottom: '6px' }}>Dataset takes priority when selected. Inline text is a fallback.</div>
                      <textarea
                        value={(editingBot as any).aiConfig?.knowledgeBase || ''}
                        onChange={e => setEditingBot({ ...editingBot, aiConfig: { ...(editingBot as any).aiConfig, knowledgeBase: e.target.value } } as any)}
                        rows={8}
                        placeholder="Paste fallback FAQs, pricing, policies here..."
                        style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', resize: 'vertical', fontFamily: 'monospace', boxSizing: 'border-box' as const }}
                      />
                      <div style={{ fontSize: '11px', marginTop: '4px', color: ((editingBot as any).aiConfig?.knowledgeBase || '').length > 8000 ? '#f59e0b' : 'var(--text-faint)' }}>
                        {((editingBot as any).aiConfig?.knowledgeBase || '').length.toLocaleString()} characters
                        {((editingBot as any).aiConfig?.knowledgeBase || '').length > 8000 ? ' — large: only the most relevant sections are sent per reply' : ''}
                      </div>
                    </>
                  )}
                </div>
                {(editingBot as any).aiChannel === 'whatsapp' && (
                  <>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: '10px', marginTop: '4px' }}>WhatsApp Credentials</div>
                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: '6px' }}>Phone Number ID</label>
                      <input value={(editingBot as any).phoneNumberId || ''} onChange={e => setEditingBot({ ...editingBot, phoneNumberId: e.target.value } as any)} placeholder="1234567890"
                        style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const }} />
                    </div>
                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: '6px' }}>Access Token</label>
                      <input type="password" value={(editingBot as any).accessToken || ''} onChange={e => setEditingBot({ ...editingBot, accessToken: e.target.value } as any)} placeholder="Already connected — leave blank to keep"
                        style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const }} />
                    </div>
                    <div style={{ marginBottom: '14px', padding: '12px', background: 'var(--bg-card)', borderRadius: '8px', border: '1px solid var(--card-border)' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: '8px' }}>Webhook URL</div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input readOnly value={webhookUrl}
                          style={{ flex: 1, padding: '8px 10px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '7px', color: 'var(--text-muted)', fontSize: '11px', outline: 'none', minWidth: 0 }} />
                        <button onClick={() => { navigator.clipboard.writeText(webhookUrl); alert('Copied!'); }}
                          style={{ padding: '8px 14px', background: '#5B4FE8', color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', flexShrink: 0 }}>Copy</button>
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '6px' }}>
                        Verify token: <code style={{ background: 'var(--bg-page)', padding: '1px 6px', borderRadius: '4px', color: 'var(--text-secondary)' }}>{editingBot.businessName}_{new Date().getFullYear()}</code>
                      </div>
                    </div>
                  </>
                )}
                {(editingBot as any).aiChannel === 'facebook' && (
                  <>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: '10px', marginTop: '4px' }}>Facebook Page Credentials</div>
                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: '6px' }}>Facebook Page ID</label>
                      <input value={(editingBot as any).fbPageId || ''} onChange={e => setEditingBot({ ...editingBot, fbPageId: e.target.value } as any)} placeholder="123456789012345"
                        style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const }} />
                    </div>
                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: '6px' }}>Page Access Token</label>
                      <input type="password" value={(editingBot as any).accessToken || ''} onChange={e => setEditingBot({ ...editingBot, accessToken: e.target.value } as any)} placeholder="Already connected — leave blank to keep"
                        style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const }} />
                    </div>
                  </>
                )}
              </>
            )}

            {/* Website allowed domains */}
            {(editingBot.botType === 'website' || (editingBot.botType === 'ai' && (editingBot as any).aiChannel === 'website')) && (
              <>
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: '8px' }}>Embed code</div>
                  <div style={{ background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', padding: '10px' }}>
                    <code style={{ fontSize: '10px', color: '#818cf8', wordBreak: 'break-all' }}>
                      {`<script src="${widgetURL}/widget.js" data-bot-id="${editingBot.id}"${API_BASE ? ` data-api-url="${API_BASE}"` : ''}></script>`}
                    </code>
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '5px' }}>Paste before the {'</body>'} tag</div>
                </div>
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: '8px' }}>Allowed websites</div>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input type="text" value={newWebsiteInput} onChange={e => setNewWebsiteInput(e.target.value)} placeholder="mybusiness.com"
                      style={{ flex: 1, padding: '8px 10px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '7px', color: 'var(--text-primary)', fontSize: '12px', outline: 'none' }} />
                    <button onClick={() => {
                      if (newWebsiteInput.trim() && !editingBot.allowedWebsites?.includes(newWebsiteInput.trim())) {
                        setEditingBot({ ...editingBot, allowedWebsites: [...(editingBot.allowedWebsites || []), newWebsiteInput.trim()] });
                        setNewWebsiteInput('');
                      }
                    }} style={{ padding: '8px 14px', background: '#5B4FE8', color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', flexShrink: 0 }}>Add</button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {(editingBot.allowedWebsites || []).map(w => (
                      <span key={w} style={{ background: 'var(--bg-element)', padding: '3px 10px', borderRadius: '14px', fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {w}
                        <button onClick={() => setEditingBot({ ...editingBot, allowedWebsites: editingBot.allowedWebsites?.filter(x => x !== w) })}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 0, lineHeight: 1, fontSize: '13px' }}>×</button>
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Payment methods */}
            {editingBot.botType !== 'ai' && (editingBot as any).botType !== 'email' && (
              <div style={{ marginBottom: '16px', marginTop: '8px' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: '10px' }}>Payment Methods</div>
                {/* Paynow */}
                <div style={{ background: 'var(--bg-card)', borderRadius: '8px', padding: '12px', marginBottom: '8px', border: '1px solid var(--card-border)' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '8px' }}>Paynow (Zimbabwe)</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '6px' }}>
                    <input value={(editingBot as any).paymentMethods?.paynow?.integrationId || ''}
                      onChange={e => setEditingBot({ ...editingBot, paymentMethods: { ...(editingBot as any).paymentMethods, paynow: { ...(editingBot as any).paymentMethods?.paynow, integrationId: e.target.value } } } as any)}
                      placeholder="Integration ID" style={{ padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px' }} />
                    <input value={(editingBot as any).paymentMethods?.paynow?.integrationKey || ''}
                      onChange={e => setEditingBot({ ...editingBot, paymentMethods: { ...(editingBot as any).paymentMethods, paynow: { ...(editingBot as any).paymentMethods?.paynow, integrationKey: e.target.value } } } as any)}
                      placeholder="Integration Key" style={{ padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px' }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '6px' }}>
                    <input value={(editingBot as any).paymentMethods?.paynow?.testEmail || ''}
                      onChange={e => setEditingBot({ ...editingBot, paymentMethods: { ...(editingBot as any).paymentMethods, paynow: { ...(editingBot as any).paymentMethods?.paynow, testEmail: e.target.value } } } as any)}
                      placeholder="Test email" style={{ padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px' }} />
                    <select value={(editingBot as any).paymentMethods?.paynow?.mode || 'test'}
                      onChange={e => setEditingBot({ ...editingBot, paymentMethods: { ...(editingBot as any).paymentMethods, paynow: { ...(editingBot as any).paymentMethods?.paynow, mode: e.target.value } } } as any)}
                      style={{ padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px' }}>
                      <option value="test">Test</option>
                      <option value="live">Live</option>
                    </select>
                  </div>
                </div>
                {/* Cash */}
                <div style={{ background: 'var(--bg-card)', borderRadius: '8px', padding: '12px', marginBottom: '8px', border: '1px solid var(--card-border)' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '8px' }}>Cash</div>
                  <input value={(editingBot as any).paymentMethods?.cash?.venue || ''}
                    onChange={e => setEditingBot({ ...editingBot, paymentMethods: { ...(editingBot as any).paymentMethods, cash: { ...(editingBot as any).paymentMethods?.cash, venue: e.target.value } } } as any)}
                    placeholder="Venue / location" style={{ width: '100%', padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px', marginBottom: '6px', boxSizing: 'border-box' as const }} />
                  <input value={(editingBot as any).paymentMethods?.cash?.instructions || ''}
                    onChange={e => setEditingBot({ ...editingBot, paymentMethods: { ...(editingBot as any).paymentMethods, cash: { ...(editingBot as any).paymentMethods?.cash, instructions: e.target.value } } } as any)}
                    placeholder="Instructions" style={{ width: '100%', padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px', boxSizing: 'border-box' as const }} />
                </div>
                {/* Bank */}
                <div style={{ background: 'var(--bg-card)', borderRadius: '8px', padding: '12px', border: '1px solid var(--card-border)' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '8px' }}>Bank Transfer</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '6px' }}>
                    <input value={(editingBot as any).paymentMethods?.bank?.bankName || ''}
                      onChange={e => setEditingBot({ ...editingBot, paymentMethods: { ...(editingBot as any).paymentMethods, bank: { ...(editingBot as any).paymentMethods?.bank, bankName: e.target.value } } } as any)}
                      placeholder="Bank name" style={{ padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px' }} />
                    <input value={(editingBot as any).paymentMethods?.bank?.accountName || ''}
                      onChange={e => setEditingBot({ ...editingBot, paymentMethods: { ...(editingBot as any).paymentMethods, bank: { ...(editingBot as any).paymentMethods?.bank, accountName: e.target.value } } } as any)}
                      placeholder="Account name" style={{ padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px' }} />
                    <input value={(editingBot as any).paymentMethods?.bank?.accountNumber || ''}
                      onChange={e => setEditingBot({ ...editingBot, paymentMethods: { ...(editingBot as any).paymentMethods, bank: { ...(editingBot as any).paymentMethods?.bank, accountNumber: e.target.value } } } as any)}
                      placeholder="Account number" style={{ padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px' }} />
                    <input value={(editingBot as any).paymentMethods?.bank?.branch || ''}
                      onChange={e => setEditingBot({ ...editingBot, paymentMethods: { ...(editingBot as any).paymentMethods, bank: { ...(editingBot as any).paymentMethods?.bank, branch: e.target.value } } } as any)}
                      placeholder="Branch (optional)" style={{ padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px' }} />
                  </div>
                  <input value={(editingBot as any).paymentMethods?.bank?.instructions || ''}
                    onChange={e => setEditingBot({ ...editingBot, paymentMethods: { ...(editingBot as any).paymentMethods, bank: { ...(editingBot as any).paymentMethods?.bank, instructions: e.target.value } } } as any)}
                    placeholder="Additional instructions (optional)" style={{ width: '100%', padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px', boxSizing: 'border-box' as const }} />
                </div>
              </div>
            )}

            {/* Save / Cancel */}
            <div style={{ display: 'flex', gap: '10px', paddingTop: '16px', borderTop: '1px solid var(--border)', marginTop: '8px' }}>
              <button onClick={() => { setShowSettings(false); setEditingBot(null); }}
                style={{ flex: 1, padding: '12px', background: 'var(--bg-element)', color: 'var(--text-muted)', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
              <button onClick={handleUpdateBot}
                style={{ flex: 2, padding: '12px', background: 'linear-gradient(135deg, #5B4FE8, #7c3aed)', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>Save changes</button>
            </div>

            {/* Danger zone */}
            <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid var(--border)' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-faint)', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: '10px' }}>Danger zone</div>
              <button onClick={() => handleDeleteBot(editingBot.id)}
                style={{ width: '100%', padding: '12px', background: '#ef444410', color: '#ef4444', border: '1px solid #ef444430', borderRadius: '10px', cursor: 'pointer', fontSize: '13px' }}>
                Delete this bot permanently
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* ─────────────────────────────────────────────
     BOT PICKER SHEET
  ───────────────────────────────────────────── */
  const BotPickerSheet = () => {
    if (!showBotPicker) return null;
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 150, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
        <div
          onClick={() => setShowBotPicker(false)}
          style={{ flex: 1 }}
        />
        <div style={{ background: 'var(--bg-sidebar)', borderRadius: '16px 16px 0 0', maxHeight: '70vh', overflowY: 'auto', paddingBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 16px 12px' }}>
            <div style={{ color: 'var(--text-primary)', fontSize: '15px', fontWeight: '700' }}>Your bots</div>
            <button onClick={() => setShowBotPicker(false)} style={{ background: 'none', border: 'none', color: 'var(--text-faint)', cursor: 'pointer', fontSize: '20px', lineHeight: 1 }}>✕</button>
          </div>
          <div style={{ height: '1px', background: 'var(--border)', marginBottom: '8px' }} />
          {bots.length === 0 && (
            <div style={{ padding: '20px 16px', color: 'var(--text-faint)', fontSize: '13px', textAlign: 'center' }}>No bots yet. Create one below.</div>
          )}
          {bots.map(bot => {
            const isSel = selectedBot?.id === bot.id;
            const payStatus = userData?.paymentStatus;
            const accountLapsed = payStatus === 'expired' || payStatus === 'failed' || payStatus === 'cancelled';
            const isWA = bot.botType === 'whatsapp' || (!bot.botType && !!bot.phoneNumberId);
            const isLocked = bot.status === 'suspended' || (isWA && (accountLapsed || hasOverdueInvoice));
            return (
              <div
                key={bot.id}
                onClick={() => {
                  if (isLocked) { navigate('/billing'); setShowBotPicker(false); return; }
                  handleSelectBot(bot);
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '14px 16px', cursor: isLocked ? 'not-allowed' : 'pointer',
                  background: isSel ? '#5B4FE815' : 'transparent',
                  borderLeft: isSel ? '3px solid #5B4FE8' : '3px solid transparent',
                  opacity: isLocked ? 0.7 : 1,
                  minHeight: '60px',
                }}
              >
                <span style={{ fontSize: '22px', filter: isLocked ? 'grayscale(1)' : 'none' }}>{botIcon(bot)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{bot.businessName}</div>
                  <div style={{ color: 'var(--text-faint)', fontSize: '11px', marginTop: '2px' }}>{botLabel(bot)}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                  {isLocked && <span style={{ background: 'rgba(220,38,38,0.8)', color: 'white', fontSize: '9px', fontWeight: '700', padding: '2px 6px', borderRadius: '20px' }}>🔒</span>}
                  <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: statusColor(bot.status) }} />
                  {isSel && <span style={{ color: '#5B4FE8', fontSize: '16px' }}>✓</span>}
                </div>
              </div>
            );
          })}
          <div style={{ height: '1px', background: 'var(--border)', margin: '8px 0' }} />
          <div
            onClick={() => { navigate('/create'); setShowBotPicker(false); }}
            style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', cursor: 'pointer', color: '#5B4FE8', minHeight: '52px' }}
          >
            <span style={{ fontSize: '22px' }}>＋</span>
            <span style={{ fontSize: '14px', fontWeight: '600' }}>New Bot</span>
          </div>
        </div>
      </div>
    );
  };

  /* ─────────────────────────────────────────────
     WELCOME STATE
  ───────────────────────────────────────────── */
  const WelcomeState = () => (
    <div style={{ padding: '40px 24px', textAlign: 'center' }}>
      <div style={{ fontSize: '56px', marginBottom: '16px' }}>🤖</div>
      <h2 style={{ color: 'var(--text-primary)', fontSize: '22px', fontWeight: '700', marginBottom: '10px' }}>
        {bots.length === 0 ? 'Create your first bot' : 'Select a bot to view it'}
      </h2>
      <p style={{ color: 'var(--text-faint)', fontSize: '14px', lineHeight: '1.6', marginBottom: '28px' }}>
        {bots.length === 0
          ? 'Set up a bot that chats with your customers 24/7 on WhatsApp, your website, or Facebook — no tech skills needed.'
          : 'Tap the bot name at the top to select one.'}
      </p>
      {bots.length === 0 && (
        <Link to="/create" style={{ display: 'inline-block', background: 'linear-gradient(135deg, #5B4FE8, #7c3aed)', color: 'white', padding: '14px 32px', borderRadius: '12px', textDecoration: 'none', fontSize: '15px', fontWeight: '600' }}>
          Create my first bot →
        </Link>
      )}
    </div>
  );

  /* ─────────────────────────────────────────────
     TRIAL BANNER
  ───────────────────────────────────────────── */
  const trialDaysLeft = userData?.paymentStatus === 'trial' && userData?.trialEndsAt
    ? Math.max(0, Math.ceil((userData.trialEndsAt - Date.now()) / 86400000))
    : 0;

  /* ─────────────────────────────────────────────
     MAIN RENDER
  ───────────────────────────────────────────── */
  return (
    <>
      <MobileLayout
        title={selectedBot?.businessName || companyName}
        headerRight={BotPickerButton}
      >
        {/* Trial banner */}
        {userData?.paymentStatus === 'trial' && userData?.trialEndsAt && (
          <div style={{ background: 'linear-gradient(90deg, #3d35b5, #7c3aed)', color: '#fff', padding: '9px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
            <span>🎁 <strong>Trial</strong> — {trialDaysLeft > 0 ? `${trialDaysLeft} day${trialDaysLeft > 1 ? 's' : ''} left` : 'ends today'}</span>
            <a href="/billing" style={{ color: '#c4b5fd', fontWeight: 600, textDecoration: 'none', fontSize: '12px' }}>Manage →</a>
          </div>
        )}

        {/* Content */}
        {!selectedBot ? (
          <WelcomeState />
        ) : (
          <>
            {/* Tab bar */}
            <div style={{
              position: 'sticky', top: 0, zIndex: 40,
              background: 'var(--bg-card)',
              borderBottom: '1px solid var(--border)',
              display: 'flex', overflowX: 'auto', gap: '2px',
              padding: '8px 12px',
              flexShrink: 0,
              WebkitOverflowScrolling: 'touch' as any,
              scrollbarWidth: 'none' as any,
            }}>
              {getTabsForBot(selectedBot).map(tab => (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id as any); setSelectedConv(null); }}
                  style={{
                    padding: '7px 14px',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    background: activeTab === tab.id ? '#5B4FE8' : 'var(--bg-element)',
                    color: activeTab === tab.id ? 'white' : 'var(--text-muted)',
                    fontSize: '12px',
                    fontWeight: activeTab === tab.id ? '600' : '400',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    minHeight: '34px',
                  }}
                >
                  {tab.icon} {tab.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            {activeTab === 'overview' && <OverviewTab bot={selectedBot} />}
            {activeTab === 'conversations' && <ConversationsTab bot={selectedBot} />}
            {activeTab === 'reports' && (
              <div style={{ padding: '16px' }}>
                <ReportsPanel bot={selectedBot} />
              </div>
            )}
            {activeTab === 'embed' && <EmbedTab bot={selectedBot} />}
          </>
        )}
      </MobileLayout>

      {/* Bot picker sheet */}
      <BotPickerSheet />

      {/* Settings overlay */}
      <SettingsModal />
    </>
  );
}
